/**
 * « Accès aux autres parcs » d'une personne (étape 4 du chantier
 * droits-multi-parcs, décisions D3 à D5 de .scratch/droits-multi-parcs/spec.md).
 *
 * Réservé au staff AKONGO (décision du 03/10) : personne d'autre ne voit les
 * parcs qui ne le concernent pas. Pour tout autre lecteur, rien n'est lu ni
 * proposé, et le serveur refuse d'ouvrir un deuxième parc à quelqu'un
 * (MULTI_PARK_STAFF_ONLY).
 *
 * Parcs proposés : tous les parcs déclarés multi-accès. La personne n'y figure
 * qu'avec le rôle admin, et seulement si chacun de ses parcs est multi-accès :
 * le serveur le vérifie (MULTI_PARK_ADMIN_ONLY, PARK_NOT_MULTI_ACCESS) et
 * l'écran le dit. État de la personne dans chaque parc : son registre, que le
 * staff lit. Son parc de rattachement ne se retire pas ici.
 */
const nonEmpty = (value) => (typeof value === "string" && value ? value : null);

const parkName = (zoo, zooId) => nonEmpty(zoo?.zooName) || zooId;

async function readDoc(ref) {
  try {
    const snap = await ref.get();
    return snap.exists ? snap.data() : null;
  } catch (_error) {
    return undefined;
  }
}

/** Parcs multi-accès proposés au staff AKONGO, triés par nom : [{ zooId, zooName }]. */
export async function proposedMultiParks({ db, viewer }) {
  if (viewer?.akongoStaff !== true) return [];
  const snap = await db.collection("zoo").where("multiParkAccess", "==", true).get();
  return snap.docs.map((doc) => ({ zooId: doc.id, zooName: parkName(doc.data(), doc.id) }))
    .sort((a, b) => a.zooName.localeCompare(b.zooName));
}

/**
 * Parcs proposés avec l'état de la personne dans chacun :
 * [{ zooId, zooName, active, home }]. `unknown` : état illisible (accès refusé).
 */
export async function loadMultiParkAccess({ db, viewer, targetUid }) {
  const parks = await proposedMultiParks({ db, viewer });
  if (!parks.length) return { parks: [] };
  const grant = await readDoc(db.collection("accessGrants").doc(targetUid));
  if (grant === undefined) return { parks: parks.map((park) => ({ ...park, active: false, home: false, unknown: true })) };
  const zoos = grant && grant.zoos ? grant.zoos : {};
  return {
    parks: parks.map((park) => ({
      ...park,
      active: zoos[park.zooId]?.status === "active",
      home: grant?.homeZooId === park.zooId,
    })),
  };
}

/** Ouvre (rôle admin) ou retire un parc à la personne, par commande serveur. */
export function setMultiParkEntry({ call, targetUid, zooId, enabled }) {
  return enabled
    ? call("accessSetZooEntry", { uid: targetUid, zooId, role: "admin" })
    : call("accessRevokeZoo", { uid: targetUid, zooId });
}

/** Code de refus du serveur (message de l'erreur de la fonction appelable), ou null. */
export function multiParkFailureCode(error) {
  const message = typeof error?.message === "string" ? error.message : "";
  const known = ["MULTI_PARK_ADMIN_ONLY", "PARK_NOT_MULTI_ACCESS", "MULTI_PARK_STAFF_ONLY", "NOT_ADMIN_OF_PERSON_PARK",
    "AKONGO_ACCOUNT", "NOT_PARK_ADMIN", "REVISION_CONFLICT", "MULTI_PARK_IN_USE", "STAFF_ONLY", "NOT_ACTIVE"];
  return known.find((code) => message.startsWith(code)) || null;
}

/** Nombre de personnes qui bloquent le retrait du réglage multi-accès (`MULTI_PARK_IN_USE:n`), ou null. */
export function multiParkInUseCount(error) {
  const match = /^MULTI_PARK_IN_USE:(\d+)$/.exec(typeof error?.message === "string" ? error.message : "");
  return match ? Number(match[1]) : null;
}
